// Inline popover picker for adding a contact to an asset (spec S4.2), extended
// with a "regrouper" mode that builds a groupement (colocation / indivision /
// couple) of several physical persons — spec 2026-07-13-01-refonte-contacts-parties.
//
// The component name is kept (`AddContactModal`) to avoid touching every caller.

import { AppIcon } from '../ui/icon'
import { mdiPlus, mdiTrashCanOutline } from '@mdi/js'
import { Button } from '../ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'
import { Label } from '../ui/label'
import { Input } from '../ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { ApiSearchableSelect } from '../ApiSearchableSelect'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useContactsSearch, useContractStakeholderTypes } from '../../hooks/useQueries'
import { useLocale } from '../../contexts/LocaleContext'
import { getLocalizedName } from '../../lib/localization'
import { SECTION_ICONS } from '../../lib/section-icons'

const USER_CODE_REGEX = /^[A-Z0-9_-]+$/
const GROUP_KINDS = ['colocation', 'indivision', 'couple'] as const

export type GroupMemberInput = { userUid: string; sharesPercent?: number }
export type GroupSubmit = {
    /** The groupement's display name — it is a party, so it needs one. */
    name: string
    members: GroupMemberInput[]
    kind: string
    stakeholderTypeUid: string
    stakeholderTypeName: string
}

interface AddContactModalProps {
    isOpen: boolean
    onOpenChange: (open: boolean) => void
    org: string
    onSubmit: (userUid: string, stakeholderTypeUid: string, stakeholderTypeName: string) => void
    /** Optional: submit a groupement (colocation / indivision / couple). */
    onSubmitGroup?: (group: GroupSubmit) => void
    isSubmitting?: boolean
    /** Overrides « Add a contact » (the Intervenants section says « Add an intervenant »). */
    triggerLabel?: string
    /** `icon` (default) is the bare + of a section header; `button` an outline button with its label. */
    triggerVariant?: 'icon' | 'button'
}

export function AddContactModal({
    isOpen,
    onOpenChange,
    org,
    onSubmit,
    onSubmitGroup,
    isSubmitting = false,
    triggerLabel: triggerLabelProp,
    triggerVariant = 'icon',
}: AddContactModalProps) {
    const [selectedUserUid, setSelectedUserUid] = useState('')
    const [selectedStakeholderTypeUid, setSelectedStakeholderTypeUid] = useState('')
    const [contactCode, setContactCode] = useState('')
    // Group mode
    const [groupMode, setGroupMode] = useState(false)
    const [groupKind, setGroupKind] = useState<string>('colocation')
    const [groupName, setGroupName] = useState('')
    const [members, setMembers] = useState<Array<{ userUid: string; quote: string }>>([
        { userUid: '', quote: '' },
        { userUid: '', quote: '' },
    ])
    const { t } = useTranslation('users')
    const { locale } = useLocale()

    const trimmedCode = contactCode.trim()
    const codeIsInvalid = trimmedCode !== '' && !USER_CODE_REGEX.test(trimmedCode)
    const normalizedCode: string | null = trimmedCode === '' ? null : trimmedCode

    const { data: stakeholderTypes } = useContractStakeholderTypes(org, { limit: 100 })
    const { searchUsers } = useContactsSearch(org, 20)

    const stakeholderTypeName = (uid: string) => {
        const st = stakeholderTypes?.find((type: any) => type.uid === uid)
        return (st && getLocalizedName(st.names, locale)) || st?.code || ''
    }

    const validMembers = members.filter((m) => m.userUid)

    const handleSubmit = () => {
        if (groupMode && onSubmitGroup) {
            if (validMembers.length < 2 || !selectedStakeholderTypeUid || !groupName.trim()) return
            onSubmitGroup({
                name: groupName.trim(),
                members: validMembers.map((m) => ({
                    userUid: m.userUid,
                    // The quote input is a percentage, and shares_percent is
                    // stored as one — no conversion.
                    sharesPercent: m.quote.trim() ? Number(m.quote) : undefined,
                })),
                kind: groupKind,
                stakeholderTypeUid: selectedStakeholderTypeUid,
                stakeholderTypeName: stakeholderTypeName(selectedStakeholderTypeUid),
            })
            return
        }
        if (!selectedUserUid || !selectedStakeholderTypeUid || codeIsInvalid) return
        void normalizedCode
        onSubmit(selectedUserUid, selectedStakeholderTypeUid, stakeholderTypeName(selectedStakeholderTypeUid))
    }

    const resetState = () => {
        setSelectedUserUid('')
        setSelectedStakeholderTypeUid('')
        setContactCode('')
        setGroupMode(false)
        setGroupKind('colocation')
        setGroupName('')
        setMembers([{ userUid: '', quote: '' }, { userUid: '', quote: '' }])
    }

    const handleOpenChange = (open: boolean) => {
        if (!open) resetState()
        onOpenChange(open)
    }

    const triggerLabel = triggerLabelProp ?? t('common:actions.add_contact', 'Add a contact')
    const canGroup = !!onSubmitGroup

    const stakeholderTypeSelect = (
        <div className="grid gap-1">
            <Label htmlFor="stakeholder-type" className="text-xs text-muted-foreground">
                {t('contacts.stakeholder_type_label', 'Type de partie prenante')}
            </Label>
            <Select value={selectedStakeholderTypeUid} onValueChange={setSelectedStakeholderTypeUid}>
                <SelectTrigger data-testid="stakeholder-type-select" className="h-8">
                    <SelectValue placeholder={t('contacts.stakeholder_type_placeholder', 'Sélectionnez un type')} />
                </SelectTrigger>
                <SelectContent>
                    {stakeholderTypes?.map((type: any) => (
                        <SelectItem key={type.uid} value={type.uid}>
                            <div className="flex items-center gap-2">
                                <AppIcon path={SECTION_ICONS.assets} size="sm" />
                                {getLocalizedName(type.names, locale) || type.code}
                            </div>
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
    )

    return (
        <Popover open={isOpen} onOpenChange={handleOpenChange}>
            <PopoverTrigger asChild>
                {triggerVariant === 'button' ? (
                    <Button type="button" variant="outline" size="sm" data-testid="create-contact-btn" aria-label={triggerLabel} title={triggerLabel}>
                        <AppIcon path={mdiPlus} size="sm" className="md:mr-1" />
                        <span className="hidden md:inline">{triggerLabel}</span>
                    </Button>
                ) : (
                    <button
                        type="button"
                        data-testid="create-contact-btn"
                        aria-label={triggerLabel}
                        title={triggerLabel}
                        className="inline-flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground active:bg-muted/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
                    >
                        <AppIcon path={mdiPlus} size="sm" />
                    </button>
                )}
            </PopoverTrigger>
            <PopoverContent className={groupMode ? 'w-96 p-3' : 'w-80 p-3'} align="end">
                <div className="grid gap-3">
                    {canGroup && (
                        <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                            <input
                                type="checkbox"
                                checked={groupMode}
                                onChange={(e) => setGroupMode(e.target.checked)}
                                data-testid="group-mode-toggle"
                                className="h-3.5 w-3.5"
                            />
                            {t('contacts.group.mode_label', 'Regrouper plusieurs personnes')}
                        </label>
                    )}

                    {!groupMode ? (
                        <>
                            <div className="grid gap-1">
                                <Label htmlFor="contact-person" className="text-xs text-muted-foreground">
                                    {t('contacts.contact_person_label', 'Contact')}
                                </Label>
                                <ApiSearchableSelect
                                    value={selectedUserUid}
                                    onValueChange={setSelectedUserUid}
                                    onSearch={searchUsers}
                                    placeholder={t('contacts.contact_person_placeholder', 'Sélectionnez un contact')}
                                    searchPlaceholder={t('contacts.contact_person_search', 'Rechercher par nom ou e-mail...')}
                                    emptyMessage={t('contacts.contact_person_empty', 'Aucun utilisateur trouvé.')}
                                    data-testid="contact-person-select"
                                    minSearchLength={2}
                                />
                            </div>

                            <div className="grid gap-1">
                                <Label htmlFor="contact-code" className="text-xs text-muted-foreground">
                                    {t('contacts.code_label')}
                                </Label>
                                <Input
                                    id="contact-code"
                                    type="text"
                                    value={contactCode}
                                    onChange={(e) => setContactCode(e.target.value)}
                                    placeholder={t('contacts.code_placeholder')}
                                    data-testid="contact-code-input"
                                    aria-invalid={codeIsInvalid}
                                    className="h-8"
                                />
                                {codeIsInvalid && (
                                    <p className="text-xs text-destructive" data-testid="contact-code-error">
                                        {t('contacts.code_invalid_format')}
                                    </p>
                                )}
                            </div>

                            {stakeholderTypeSelect}
                        </>
                    ) : (
                        <>
                            <div className="grid grid-cols-2 gap-2">
                                <div className="grid gap-1">
                                    <Label className="text-xs text-muted-foreground">
                                        {t('contacts.group.kind_label', 'Type de groupement')}
                                    </Label>
                                    <Select value={groupKind} onValueChange={setGroupKind}>
                                        <SelectTrigger data-testid="group-kind-select" className="h-8">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {GROUP_KINDS.map((k) => (
                                                <SelectItem key={k} value={k}>
                                                    {t(`contacts.group.kind.${k}`)}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="grid gap-1">
                                    <Label htmlFor="group-name" className="text-xs text-muted-foreground">
                                        {t('contacts.group.name_label')}
                                    </Label>
                                    <Input
                                        id="group-name"
                                        type="text"
                                        value={groupName}
                                        onChange={(e) => setGroupName(e.target.value)}
                                        placeholder={t('contacts.group.name_placeholder')}
                                        data-testid="group-name-input"
                                        className="h-8"
                                    />
                                </div>
                            </div>

                            <div className="grid gap-2">
                                <Label className="text-xs text-muted-foreground">
                                    {t('contacts.group.members_label', 'Membres')}
                                </Label>
                                {members.map((m, i) => (
                                    <div key={i} className="flex items-center gap-2">
                                        <div className="flex-1">
                                            <ApiSearchableSelect
                                                value={m.userUid}
                                                onValueChange={(v) =>
                                                    setMembers((prev) => prev.map((mm, j) => (j === i ? { ...mm, userUid: v } : mm)))
                                                }
                                                onSearch={searchUsers}
                                                placeholder={t('contacts.contact_person_placeholder', 'Sélectionnez un contact')}
                                                searchPlaceholder={t('contacts.contact_person_search', 'Rechercher...')}
                                                emptyMessage={t('contacts.contact_person_empty', 'Aucun utilisateur trouvé.')}
                                                data-testid={`group-member-select-${i}`}
                                                minSearchLength={2}
                                            />
                                        </div>
                                        {groupKind === 'indivision' && (
                                            <div className="flex items-center gap-1">
                                                <Input
                                                    type="number"
                                                    min={0}
                                                    max={100}
                                                    value={m.quote}
                                                    onChange={(e) =>
                                                        setMembers((prev) => prev.map((mm, j) => (j === i ? { ...mm, quote: e.target.value } : mm)))
                                                    }
                                                    placeholder="%"
                                                    data-testid={`group-member-quote-${i}`}
                                                    className="h-8 w-16"
                                                />
                                                <span className="text-xs text-muted-foreground">%</span>
                                            </div>
                                        )}
                                        {members.length > 2 && (
                                            <button
                                                type="button"
                                                onClick={() => setMembers((prev) => prev.filter((_, j) => j !== i))}
                                                data-testid={`group-member-remove-${i}`}
                                                className="text-muted-foreground hover:text-destructive"
                                                aria-label={t('common:actions.remove', 'Remove')}
                                            >
                                                <AppIcon path={mdiTrashCanOutline} size="sm" />
                                            </button>
                                        )}
                                    </div>
                                ))}
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 justify-start px-1"
                                    onClick={() => setMembers((prev) => [...prev, { userUid: '', quote: '' }])}
                                    data-testid="group-add-member"
                                >
                                    <AppIcon path={mdiPlus} size="xs" className="mr-1" />
                                    {t('contacts.group.add_member', 'Ajouter une personne')}
                                </Button>
                            </div>

                            {stakeholderTypeSelect}
                        </>
                    )}

                    <div className="flex justify-end gap-2 pt-1">
                        <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenChange(false)}
                            className="h-7"
                        >
                            {t('contacts.cancel_label', 'Annuler')}
                        </Button>
                        <Button
                            type="button"
                            onClick={handleSubmit}
                            disabled={
                                isSubmitting ||
                                (groupMode
                                    ? validMembers.length < 2 || !selectedStakeholderTypeUid || !groupName.trim()
                                    : !selectedUserUid || !selectedStakeholderTypeUid || codeIsInvalid)
                            }
                            data-testid="create-contact-submit"
                            size="sm"
                            className="h-7"
                        >
                            {isSubmitting
                                ? t('contacts.submit_creating', 'Création...')
                                : t('contacts.submit_label', 'Ajouter')}
                        </Button>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    )
}
